import * as React from "react"
import { ArticleCard } from "@/components/brand-kit/article-card"
import { BLOG_HERO_FALLBACK } from "@/lib/constants"
import { SectionHeader } from "./section-header"
import type { BrandArticle } from "./types"

interface RelatedArticlesProps {
  brandName: string
  articles: BrandArticle[]
}

/** Keep only articles with a real hero image — `image` must be set AND
 *  not the sitewide fallback logo (`fallback-blog-hero.png`, which is just
 *  the Fridge.com logo on a beige plate). Showing the same fallback across
 *  every card makes the section look like a placeholder rather than real
 *  editorial content, so we drop those rather than render them. */
function hasRealImage(a: BrandArticle): boolean {
  if (!a.image) return false
  const url = a.image.trim()
  if (url.length === 0) return false
  if (url === BLOG_HERO_FALLBACK) return false
  // Defensive against any cached/legacy variants that point at the same
  // file via a different storage path / query string.
  if (/fallback-blog-hero\.(png|jpg|jpeg|webp)/i.test(url)) return false
  return true
}

/**
 * "Related Articles" section — replaces the original Buying Guides slot.
 *
 * Uses the brand-kit `ArticleCard` so the cards match the rest of the site's
 * editorial surfaces. Filters out articles without a real hero image (null,
 * empty string, or the BLOG_HERO_FALLBACK logo placeholder) so every visible
 * card carries a distinct photo. If filtering empties the pool the whole
 * section hides rather than rendering an awkward 1- or 2-card row.
 */
export function RelatedArticles({ brandName, articles }: RelatedArticlesProps) {
  const visible = articles.filter(hasRealImage).slice(0, 4)
  if (visible.length === 0) return null
  return (
    <section className="my-12">
      <SectionHeader
        eyebrow="Keep reading"
        title="Related Articles"
        description={`More guides, comparisons, and how-tos that pair well with ${brandName} shopping.`}
        action={{ type: "link", label: "All articles", href: "/blogs/news" }}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {visible.map(a => {
          const date = new Date(a.published_at)
          const dateLabel = isNaN(date.getTime())
            ? undefined
            : date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
          return (
            <ArticleCard
              key={a.id}
              href={`/blogs/news/${a.slug}`}
              imageUrl={a.image!}
              badge="Article"
              title={a.title}
              author={a.author_name ?? undefined}
              authorImageUrl={a.authorImageUrl ?? null}
              excerpt={a.excerpt}
              date={dateLabel}
            />
          )
        })}
      </div>
    </section>
  )
}
